import { arktypeResolver } from '@hookform/resolvers/arktype';
import { MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { CardSelect } from '@/components/molecules/CardSelect';
import { CardSelectItem } from '@/components/molecules/CardSelectItem';
import { OneOf } from '@/components/molecules/OneOf';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import countries from '@/data/countries';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { PaymentMethod, usePaymentMethodsByCountry } from '@/hooks/usePaymentMethods';
import { useTranslator } from '@/hooks/useTranslator';
import { addContactFlowModalSchema, NewContactType } from '@/schemas/addContactFlowModal';
import { CountryType } from '@/types/country';
import { FiatAccountType } from '@/types/fiatAccountType';
import { SelectItem } from '@/types/fields';
import { ModalFlowContext } from '@/types/modalFlowContext';
import { OneOfOption } from '@/types/oneOfOption';

type AddContactForm = typeof addContactFlowModalSchema.infer;

export const AddContactFlowModal: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const modal = useOutletContext<ModalFlowContext>();
  const tr = useTranslator();

  const goBack = React.useCallback((): void => {
    modal.close();
  }, [modal]);

  const onSubmit = React.useCallback(
    (form: AddContactForm): void => {
      const { contactType, country, paymentMethod } = form;
      if (contactType === NewContactType.internal) {
        navigate(`/contacts/new/${contactType}/country/${country}/account-type/${paymentMethod}`);
      } else if (contactType === NewContactType.external) {
        navigate(
          `/contacts/new/${contactType}/country/${country}/account-type/${paymentMethod}/recipient-details`,
        );
      }
    },
    [navigate],
  );

  const options = React.useMemo(
    (): readonly OneOfOption[] => [
      {
        label: tr('YOUR_ACCOUNT'),
        value: 'internal',
      },
      {
        label: tr('EXTERNAL_RECIPIENT'),
        value: 'external',
      },
    ],
    [tr],
  );

  const { register, control, watch, handleSubmit } = useFormWithErrors<AddContactForm>({
    defaultValues: {
      contactType: NewContactType.unset,
      country: CountryType.unset,
      paymentMethod: FiatAccountType.unset,
    },
    resolver: arktypeResolver(addContactFlowModalSchema),
  });

  const [contactType, country] = watch(['contactType', 'country']);

  // We remove wire because, on this list, we treat "ACH" as "ACH or Wire".
  const paymentMethods = usePaymentMethodsByCountry(country).filter(
    (method: PaymentMethod): boolean => method.accountType !== FiatAccountType.wire,
  );

  const createPaymentMethodTitle = React.useCallback(
    (accountType: FiatAccountType): string | React.ReactElement => {
      const title: string = tr(cardTitles[accountType]);

      if (accountType === FiatAccountType.swift) {
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{title}</Typography>
            <Tooltip
              title={tr('SWIFT_CURRENCY_INSTRUCTED_VERSUS_SETTLEMENT_TOOLTIP')}
              placement="right">
              <InfoIcon fill="currentColor" width={24} height={24} />
            </Tooltip>
          </Stack>
        );
      }

      return title;
    },
    [tr],
  );

  const createPaymentMethodDescription = React.useCallback(
    (method: PaymentMethod): string => {
      let description = `${tr('ADD_CONTACT_FLOW_MODAL_CURRENCY_DESCRIPTION', method.currency)}`;

      if (method.accountType === FiatAccountType.swift) {
        description += ` (instructed)`;
      }

      return description;
    },
    [tr],
  );

  return (
    <ApplicationModal title={tr('ADD_NEW_CONTACT_WHICH_TYPE')} onClose={goBack}>
      <Box
        onSubmit={handleSubmit(onSubmit)}
        component="form"
        display="flex"
        flexDirection="column"
        gap={6}>
        <OneOf
          options={options}
          {...register({ name: 'contactType', label: tr('CONTACT_TYPE') })}
        />
        {contactType !== NewContactType.unset ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography fontSize={16} fontWeight={400} lineHeight="24px">
              {tr('DESTINATION_COUNTRY')}
            </Typography>
            <TextField
              value={country}
              select={true}
              {...register({ name: 'country', label: tr('COUNTRY') })}>
              {countries.map(
                (country: SelectItem<CountryType>): React.ReactElement => (
                  <MenuItem key={country.value} value={country.value}>
                    {tr(country.label)}
                  </MenuItem>
                ),
              )}
            </TextField>
          </Box>
        ) : null}
        {country ? (
          <ControllerWithError
            name="paymentMethod"
            label={tr('PAYMENT_METHOD')}
            control={control}
            render={({
              field,
            }: ControllerWithErrorParam<AddContactForm, 'paymentMethod'>): React.ReactElement => (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography fontSize={16} fontWeight={400} lineHeight="24px">
                  {tr('SELECT_PAYMENT_METHOD')}
                </Typography>
                <CardSelect
                  value={field.value}
                  error={field.error ? field.helperText : null}
                  onChange={field.onChange}>
                  {paymentMethods.map(
                    (method: PaymentMethod): React.ReactElement => (
                      <CardSelectItem
                        key={method.accountType}
                        value={method.accountType}
                        title={createPaymentMethodTitle(method.accountType)}
                        description={createPaymentMethodDescription(method)}
                      />
                    ),
                  )}
                </CardSelect>
              </Box>
            )}
          />
        ) : null}

        <Button type="submit" variant="dark" fullWidth={true}>
          {tr('NEXT')}
        </Button>
      </Box>
    </ApplicationModal>
  );
};

const cardTitles: Record<FiatAccountType, string> = {
  [FiatAccountType.cop]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_COP',
  [FiatAccountType.cvu]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_CVU',
  [FiatAccountType.pix]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_PIX',
  [FiatAccountType.ach]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_ACH',
  [FiatAccountType.wire]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_WIRE',
  [FiatAccountType.swift]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_SWIFT',
  [FiatAccountType.spei]: 'ADD_CONTACT_FLOW_MODAL_PAYMENT_METHOD_SPEI',
  // This should not happen ever
  [FiatAccountType.unset]: '',
};
